import React, { ChangeEvent, FC, useCallback, useEffect, useRef, useState } from 'react';

import { Controller, useForm } from 'react-hook-form';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { z } from 'zod';
import _ from 'lodash';
import AsyncSelect from 'react-select/async';

import { Button } from 'components/ui/button';
import { AnimatedButton, AnimatedButtonState } from 'components/ui/AnimatedButton';
import { AsteriksAfterClassName, Input } from 'components/ui/input';
import { ArrayField } from 'components/ui/ArrayField';
import { Calendly, CheckCircleIcon, CloseIcon, Github, Instagram, Linkedin, Tiktok, X } from 'components/icons/v2';
import { ControlComponent, MenuComponent, OptionComponent, PlaceholderComponent } from 'components/select';
import { Avatar } from '../../_components/Avatar';
import { toBase64 } from 'utils/images';
import { UserNode } from 'generated/types';
import { UserQuery, LoadCitiesMutation} from 'generated/operations';
import { useLoadCitiesMutation, useSaveImageProfileMutation, useUpdateBaseProfileMutation } from 'generated/hooks';
import { useFormErrorHandling } from 'hooks/useFormErrorHandling';
import { useToast } from 'hooks/useToast';
import { TabIds } from './ProfileForm';
import { useAdditionalFormSpace } from '../_hooks/useAdditionalFormSpace';
import { Textarea } from 'components/ui/textarea';
import { ProudOfItemField } from './ProudOfItemField';

type EditUserNode = NonNullable<UserQuery['user']>;
type LoadCityNode = NonNullable<LoadCitiesMutation['loadCities']>['cities'][number];
type LoadCitiesNode = LoadCityNode[];

interface IGeneralInformation {
  currentTab: TabIds;
  user: EditUserNode;
  cities: LoadCitiesNode;
  onSetDirtyForm: (formName: TabIds, isDirty: boolean) => void;
  onUpdateUser: (user: EditUserNode) => void;
}

interface IGeneralInformationForm {
  image: string;
  fullName: string;
  city: {
    id: string,
    name: string,
  };
  dreams: Array<{
    title: string;
    achieved: boolean;
  }>;
  videoUrl?: string;
  services: Array<{
    title: string;
    price?: number | null | undefined;
  }>;
  proudOf: Array<{
    title: string;
    url: string;
    description?: string | undefined;
    imageUrl?: string | null | undefined;
  }>;
  about: string;
  upTo?: string;
  linkTwitter?: string;
  linkInstagram?: string;
  linkLinkedin?: string;
  linkTiktok?: string;
  linkGithub?: string;
  linkCalendly?: string;
}

const socialMediaRegex = {
  twitter: /^https:\/\/(www\.)?x\.com\/[a-zA-Z0-9_]{1,15}\/?$/,
  instagram: /^https:\/\/(www\.)?instagram\.com\/[a-zA-Z0-9._]{1,30}\/?$/,
  linkedin: /^https:\/\/(www\.)?linkedin\.com\/in\/[a-zA-Z0-9а-яА-ЯёЁ\-_%]+\/?$/,
  tiktok: /^https:\/\/(www\.)?tiktok\.com\/@?[a-zA-Z0-9._]{1,30}\/?$/,
  github: /^https:\/\/(www\.)?github\.com\/[a-zA-Z0-9-]{1,39}\/?$/,
  calendly: /^https:\/\/(www\.)?calendly\.com\/[a-zA-Z0-9_-]{1,64}\/?$/
};

const generalInformationSchema = z.object({
  image: z.string(),
  about: z.string().min(1, 'Your Story & Superpowers is required'),
  fullName: z.string().min(1, 'Full Name is required'),
  city: z.object({
    id: z.string().min(1, { message: 'City field is required' }),
    name: z.string()
  }, { message: 'City field is required' }).required({ id: true }),
  dreams: z.array(
    z.object({
      title: z.string().min(1, 'Please enter your dream'),
      achieved: z.boolean()
    })
  ).min(1, 'Please add at least one dream'),
  videoUrl: z
    .url('Please enter a valid video URL')
    .optional()
    .or(z.literal('')
  ),
  services: z.array(
    z.object({
      title: z.string().min(1, 'Required'),
      price: z
        .number()
        .optional()
        .nullable()
        .transform((val) => {
          if (val === null || val === undefined) return null;
          return isNaN(val) ? undefined : val;
        })
    })
  ),
  proudOf: z.array(
    z.object({
      title: z.string(),
      url: z.url('Please enter a valid URL').min(1, 'URL is required'),
      description: z.string().optional(),
      imageUrl: z.string().nullable().optional()
    })
  ),
  upTo: z.string().optional(),
  linkTwitter: z
    .string()
    .regex(socialMediaRegex.twitter, 'Please enter a valid URL for Twitter')
    .optional()
    .or(z.literal('')),
  linkInstagram: z
    .string()
    .regex(socialMediaRegex.instagram, 'Please enter a valid URL for Instagram')
    .optional()
    .or(z.literal('')),
  linkLinkedin: z
    .string()
    .regex(socialMediaRegex.linkedin, 'Please enter a valid URL for LinkedIn')
    .optional()
    .or(z.literal('')),
  linkTiktok: z
    .string()
    .regex(socialMediaRegex.tiktok, 'Please enter a valid URL for TikTok')
    .optional()
    .or(z.literal('')),
  linkGithub: z
    .string()
    .regex(socialMediaRegex.github, 'Please enter a valid URL for GitHub')
    .optional()
    .or(z.literal('')),
  linkCalendly: z
    .string()
    .regex(socialMediaRegex.calendly, 'Please enter a valid URL for Calendly')
    .optional()
    .or(z.literal(''))
});

const dreamPlaceholders = [
  'Launch a founder hub in Mexico City',
  'Ride a motorcycle across Vietnam',
  'Speak at TED',
  'Write a novel',
  'Build a farm in Costa Rica',
  'Learn to play jazz piano',
  'Run a marathon',
  'Start a non-profit',
  'Design my own clothing line',
  'Climb Mount Kilimanjaro',
  'Direct an independent film',
  'Open a coffee shop in Paris',
  'Become fluent in Mandarin',
  'Sail across the Atlantic',
  'Create a viral podcast series',
  'Build a tiny home and live off-grid',
  'Photograph wildlife in the Serengeti',
  'Compose a symphony',
  'Launch a successful tech startup',
  'Teach English in rural Thailand',
  'Master the art of sushi making in Japan',
  'Restore a classic car',
  'Hike the entire Appalachian Trail',
  'Learn to freedive to 100 feet',
  'Perform stand-up comedy at the Comedy Store',
  'Build a treehouse retreat center',
  'Complete an Ironman triathlon',
  'Study meditation with monks in Tibet',
  'Design and build my dream house',
  'Learn traditional blacksmithing',
  'Backpack through South America for a year'
];

const servicePlaceholders = [
  'What service do you offer?',
  'How can you help others succeed?',
  'What expertise do you provide?',
  'What problem do you solve?',
  'What value do you create?',
  'What skill can you teach?',
  'What solution do you deliver?',
  'What transformation do you enable?',
  'What outcome do you guarantee?',
  'What experience do you craft?'
];


export const GeneralInformation: FC<IGeneralInformation> = (props) => {
  const {
    user,
    currentTab,
    onSetDirtyForm,
    onUpdateUser
  } = props;

  const [savedUser, setSavedUser] = useState<IGeneralInformationForm>({
    image: user.image ?? '',
    fullName: `${user.firstName} ${user.lastName}`,
    about: user.about ?? '',
    city: user.city ? { id: user.city.id, name: user.city.partialName } : { id: '', name: '' },
    dreams: user.dreams ?? [],
    videoUrl: user.videoUrl ?? '',
    services: user.services ?? [],
    proudOf: (user.proudOf ?? []).map(item => ({
      title: item.title || '',
      url: item.url || '',
      description: item.description || undefined,
      imageUrl: item.imageUrl || undefined
    })),
    upTo: user.upTo ?? '',
    linkLinkedin: user?.linkLinkedin ?? '',
    linkInstagram: user?.linkInstagram ?? '',
    linkTwitter: user?.linkTwitter ?? '',
    linkGithub: user?.linkGithub ?? '',
    linkCalendly: user?.linkCalendly ?? '',
    linkTiktok: user?.linkTiktok ?? ''
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    setError,
    setValue
  } = useForm<IGeneralInformationForm>({
    mode: 'all',
    shouldFocusError: true,
    values: {
      ...savedUser
    },
    resolver: standardSchemaResolver(generalInformationSchema)
  });

  const { handleToast } = useToast();
  const { handleErrors } = useFormErrorHandling();
  const [saveButtonState, setSaveButtonState] = useState<AnimatedButtonState>('idle');

  const [, saveImageProfile] = useSaveImageProfileMutation();
  const [, updateBaseProfile] = useUpdateBaseProfileMutation();
  const [, loadCities] = useLoadCitiesMutation();

  const checkIsDirty = useCallback((value1: Omit<IGeneralInformationForm, 'image'>, value2: Omit<IGeneralInformationForm, 'image'>) => {
    return !_.isEqual(value1, value2);
  }, []);

  const onError = useCallback(() => {
    setError('root', { message: 'Fix the highlighted fields and save again' });
  }, [setError, errors.root?.message]);

  const onSubmit = useCallback(async (data: IGeneralInformationForm) => {
    const { image, ...updatedData } = data;
    const { image: savedImage, ...savedUserData } = savedUser;

    setSaveButtonState('loading');

    try {
      if (data.image !== savedUser.image) {
        const imgResponse = await saveImageProfile({
          image: data.image
        });

        if (imgResponse.error) {
          setSaveButtonState('idle');
          return handleErrors(imgResponse.error, setError);
        }

        setSavedUser(prevState => ({ ...prevState, image: imgResponse.data?.saveImageProfile?.image ?? '' }));
      }

      if (checkIsDirty(updatedData, savedUserData)) {
        const [firstName, ...lastName] = updatedData.fullName.split(' ');
        const resp = await updateBaseProfile({
          ...updatedData,
          cityId: updatedData.city?.id ?? '',
          firstName,
          lastName: lastName.join(' '),
        });

        if (resp.error) {
          setSaveButtonState('idle');
          return handleErrors(resp.error, setError, {
            'video_url': 'videoUrl'
          });
        }
        if (!resp?.data?.updateBaseProfile?.user) {
          setSaveButtonState('idle');
          setError('root', { message: 'Unexpected error happened, please try again later' });
          return
        }

        const city = resp?.data.updateBaseProfile.user.city;

        setSavedUser(prevState => ({
          ...prevState,
          fullName: `${resp.data?.updateBaseProfile?.user?.firstName ?? ''} ${resp.data?.updateBaseProfile?.user?.lastName ?? ''}`,
          about: resp.data?.updateBaseProfile?.user?.about ?? '',
          dreams: resp.data?.updateBaseProfile?.user?.dreams ?? [],
          videoUrl: resp.data?.updateBaseProfile?.user?.videoUrl ?? '',
          services: resp.data?.updateBaseProfile?.user?.services ?? [],
          proudOf: (resp.data?.updateBaseProfile?.user?.proudOf ?? []).map(item => ({
            title: item.title,
            url: item.url || '',
            description: item.description || undefined,
            imageUrl: item.imageUrl || undefined
          })),
          city: city ? { id: city.id, name: city.partialName } : { id: '', name: '' },
          upTo: resp.data?.updateBaseProfile?.user?.upTo ?? '',
          linkLinkedin: resp.data?.updateBaseProfile?.user?.linkLinkedin ?? '',
          linkInstagram: resp.data?.updateBaseProfile?.user?.linkInstagram ?? '',
          linkTwitter: resp.data?.updateBaseProfile?.user?.linkTwitter ?? '',
          linkGithub: resp.data?.updateBaseProfile?.user?.linkGithub ?? '',
          linkCalendly: resp.data?.updateBaseProfile?.user?.linkCalendly ?? '',
          linkTiktok: resp.data?.updateBaseProfile?.user?.linkTiktok ?? ''
        }));
      }

      onUpdateUser({
        ...user as UserNode,
        image: data.image,
        firstName: data.fullName.split(' ')[0],
        lastName: data.fullName.split(' ').slice(1).join(' '),
        headline: data.about ?? '',
        about: data.about ?? '',
        city: data.city ? {
          id: data.city.id ?? '',
          name: data.city.name ?? '',
          partialName: data.city.name ?? ''
        } : undefined,
        videoUrl: data.videoUrl ?? '',
        upTo: data.upTo ?? '',
        services: data.services ?? [],
        proudOf: data.proudOf.map(item => ({
          title: item.title,
          url: item.url || null,
          imageUrl: item.imageUrl || null,
          text: item.description || null
        })),
        linkTwitter: data.linkTwitter ?? '',
        linkInstagram: data.linkInstagram ?? '',
        linkLinkedin: data.linkLinkedin ?? '',
        linkTiktok: data.linkTiktok ?? '',
        linkGithub: data.linkGithub ?? '',
        linkCalendly: data.linkCalendly ?? '',
      });

      setSaveButtonState('success');
    } catch (error) {
      console.error(error);
      handleToast('Oops, something went wrong', 'error');
      setSaveButtonState('idle');
    }
  }, [checkIsDirty, handleErrors, handleToast, onUpdateUser, saveImageProfile, savedUser, setError, updateBaseProfile, user]);

  useEffect(() => {
    onSetDirtyForm(TabIds.GENERAL, isDirty);
  }, [isDirty, onSetDirtyForm]);

  useEffect(() => {
    if (currentTab !== TabIds.GENERAL) {
      onSetDirtyForm(TabIds.GENERAL, false);
    }
  }, [currentTab, onSetDirtyForm]);

  useEffect(() => {
    if (errors.dreams?.message || errors.dreams?.root?.message) {
      const errorElement = document.querySelector('.dreams-array-field');
      errorElement?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  }, [errors.dreams]);

  const formRef = useRef<HTMLFormElement>(null);
  const { handleFocus, handleBlur } = useAdditionalFormSpace(formRef);

  return (
    <form onSubmit={handleSubmit(onSubmit, onError)} className="flex flex-col w-full gap-6" ref={formRef}>
      <Controller
        name="image"
        control={control}
        render={({ field: { onChange, ...field } }) => (
          <Avatar
            width={140}
            height={140}
            defaultIconHeight={80}
            defaultIconWidth={80}
            isEditable
            user={user}
            avatar={field.value}
            alt={`${user?.firstName} ${user?.lastName} - Avatar`}
            wrapperClassnames="mx-auto"
            containerClassname="text-display-xl text-gray-500 font-semibold"
            {...field}
            accept="image/png, image/gif, image/jpeg"
            onChange={async (e: ChangeEvent<HTMLInputElement>) => {
              const files = e.target.files;
              if (files && files.length > 0) {
                const base64 = await toBase64(files[0]);

                setValue('image', base64, { shouldDirty: true });
              }
            }}
          />
        )}
      />
      <Controller
        control={control}
        name="fullName"
        render={({ field: { onBlur, ...field }, formState: { errors } }) => (
          <Input
            label="Full Name"
            required={true}
            placeholder="Enter full name"
            containerClassName="bg-white"
            className="bg-white"
            errorText={errors.fullName?.message ?? ''}
            {...field}
            onFocus={handleFocus}
            onBlur={() => handleBlur(onBlur)}
          />
        )}
      />
      <Controller
        control={control}
        name="city"
        render={({ field, formState: { errors } }) => (
          <div className="flex flex-col gap-sm flex-1">
            <label
              className={`font-medium text-small max-w-max flex text-gray-900 relative ${AsteriksAfterClassName}`}
              htmlFor="city-select"
            >
              Where are you based?
            </label>
            <AsyncSelect
              inputId="city-select"
              isClearable
              cacheOptions
              isSearchable
              className="w-full"
              defaultOptions={(props.cities ?? []).map(city => ({
                id: city.id,
                name: city.partialName
              }))}
              getOptionLabel={(c) => c.name}
              getOptionValue={(c) => c.id}
              loadOptions={(inputValue, callback) => {
                try {
                  loadCities({
                    search: inputValue
                  }).then(response => {
                    const res = (response.data?.loadCities?.cities ?? []).map(city => ({
                      id: city.id,
                      name: city.partialName
                    }));
                    callback(res);
                  });

                } catch (error) {
                  console.error(error);
                }
              }}
              {...field}
              components={{
                Control: ControlComponent,
                Option: OptionComponent,
                Menu: MenuComponent,
                Placeholder: PlaceholderComponent
              }}
              error={errors.city?.message}
            />
            {errors.city &&
              <p className={`text-xs text-error-600`}>{errors.city.message}</p>
            }
            {errors.city?.id &&
              <p className={`text-xs text-error-600`}>{errors.city.id.message}</p>
            }
          </div>
        )}
      />
      <Controller
        control={control}
        name="about"
        render={({ field: { onBlur, ...field }, formState: { errors } }) => (
          <Textarea
            label="Your Story & Superpowers"
            required={true}
            placeholder="I build products and brands that bring people together - blending strategy, design, and storytelling to help ideas scale."
            errorText={errors.about?.message ?? ''}
            {...field}
            onFocus={handleFocus}
            onBlur={() => handleBlur(onBlur)}
          />
        )}
      />
      <ArrayField
        name="dreams"
        control={control}
        label="Your Dreams & Milestones"
        defaultItem={{ title: '', achieved: false }}
        errorText={errors?.dreams?.root?.message || errors.dreams?.message}
        className={'dreams-array-field'}
        renderItem={({ field, index, onRemove }) => (
          <>
            <Input
              id={field.name}
              placeholder={dreamPlaceholders[index % dreamPlaceholders.length]}
              errorText={errors?.dreams?.[index]?.title?.message || ''}
              containerClassName={'overflow-visible'}
              leftIcon={
                <>
                  <Button
                    type='button'
                    size='x-small'
                    className={
                      '!p-0 h-[20px] w-[20px] tltp-wrapper ' +
                      `${field.value?.achieved ? 'hover:text-brand-400' : 'hover:text-brand-400'}`
                    }
                    variant={field.value?.achieved ? 'nakedBrandLink' : 'nakedGrayLink'}
                    onClick={() => {
                      const newValue = { ...field.value };
                      newValue.achieved = !newValue.achieved;
                      field.onChange(newValue);
                    }}
                  >
                    <div className='tltp-content'>
                    {field.value?.achieved ? 'Not yet' : 'Achieved!'}
                  </div>
                    {field.value?.achieved ? (
                      <CheckCircleIcon className={'text-brand-500 hover:text-brand-400'} />
                    ) : (
                      <div
                        className={'h-[14px] w-[14px] border border-1 border-gray-400 rounded-full text-gray-400 hover:bg-brand-100 hover:border-brand-400'}></div>
                    )}
                  </Button>

                </>
              }
              rightIcon={
                <Button
                  size='x-small'
                  variant='nakedBrandLink'
                  type='button'
                  onClick={onRemove}
                  className='!p-0 text-gray-400 flex-shrink-0 tltp-wrapper'
                >
                  <div className={'tltp-content'}>Remove</div>
                  <CloseIcon width={20} height={20} className={'text-gray-400 hover:text-gray-600'}/>
                </Button>
              }
              {...field}
              value={field.value.title || ''}
              onChange={(e) => {
                const newValue = { ...field.value };
                newValue.title = e.target.value;
                field.onChange(newValue);
              }}
            />
          </>
        )}
        addButtonText="Add a dream"
      />
      <Controller
        control={control}
        name="videoUrl"
        render={({ field: { onBlur, ...field }, formState: { errors } }) => (
          <Input
            label="Introduce yourself (Video)"
            placeholder='https://youtube.com ... or vimeo.com... or loom.com...'
            errorText={errors.videoUrl?.message ?? ''}
            {...field}
            onFocus={handleFocus}
            onBlur={() => handleBlur(onBlur)}
          />
        )}
      />
      <ArrayField
        name="services"
        control={control}
        label="How I can help"
        defaultItem={{ title: '', price: null }}
        renderItem={({ field, index, onRemove }) => (
          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                placeholder={servicePlaceholders[index % servicePlaceholders.length]}
                errorText={errors?.services?.[index]?.title?.message || ''}
                {...field}
                value={field.value.title || ''}
                onChange={(e) => {
                  const newValue = { ...field.value };
                  newValue.title = e.target.value;
                  field.onChange(newValue);
                }}
              />
            </div>
            <div className={'py-[9px] text-gray-700 text-medium'}>$</div>
            <div className="w-36">
              <Input
                placeholder="Price"
                type={'number'}
                errorText={errors?.services?.[index]?.price?.message || ''}
                {...field}
                className={'w-full'}
                value={field.value.price || ''}
                onChange={(e) => {
                  const newValue = { ...field.value };
                  const inputValue = e.target.value;
                  newValue.price = inputValue === '' ? null : parseFloat(inputValue);
                  field.onChange(newValue);
                }}
              />
            </div>
            <Button
              size='x-small'
              variant='nakedBrandLink'
              type='button'
              onClick={onRemove}
              className='!p-0 h-[44px] text-gray-400 flex-shrink-0 tltp-wrapper'
            >
              <div className={'tltp-content'}>Remove</div>
              <CloseIcon width={20} height={20} className={'text-gray-400 hover:text-gray-600'} />
            </Button>
          </div>
        )}
        addButtonText="Share"
      />
      <ArrayField
        name="proudOf"
        control={control}
        label="What I'm proud of"
        defaultItem={{ title: '', url: '' }}
        renderItem={({ field, index, onRemove }) => (
          <ProudOfItemField
            {...field}
            onRemove={onRemove}
            urlError={errors?.proudOf?.[index]?.url?.message}
          />
        )}
        addButtonText="Drop in a link"
      />
      <Controller
        control={control}
        name="upTo"
        render={({ field: { onBlur, ...field }, formState: { errors } }) => (
          <Textarea
            label="What I'm up to"
            rows={7}
            placeholder="What are you building, exploring, or learning right now?"
            errorText={errors.upTo?.message ?? ''}
            {...field}
            onFocus={handleFocus}
            onBlur={() => handleBlur(onBlur)}
          />
        )}
      />
      <Controller
        control={control}
        name="linkTwitter"
        render={({ field: { onBlur, ...field }, formState: { errors } }) => (
          <div className="flex gap-4 items-center">
            <label htmlFor='twitterURL-in'><X /></label>
            <Input
              id={"twitterURL-in"}
              containerClassName="bg-white"
              className="bg-white"
              placeholder="https://x.com/username"
              errorText={errors.linkTwitter?.message ?? ''}
              rightIcon={(
                <span
                  className="text-medium text-gray-900 font-semibold cursor-pointer"
                  onClick={async () => {
                    try {
                      const text = await navigator.clipboard.readText();
                      setValue('linkTwitter', text, { shouldDirty: true, shouldValidate: true });
                    } catch (error) {
                      console.error(error);
                    }
                  }}
                >
                  Paste
                </span>
              )}
              {...field}
              onFocus={handleFocus}
              onBlur={() => handleBlur(onBlur)}
            />
          </div>
        )}
      />
      <Controller
        control={control}
        name="linkInstagram"
        render={({ field: { onBlur, ...field }, formState: { errors } }) => (
          <div className="flex gap-4 items-center">
            <label htmlFor='instagramURL-in'><Instagram /></label>
            <Input
              id={"instagramURL-in"}
              containerClassName="bg-white"
              className="bg-white"
              placeholder="https://instagram.com/username"
              errorText={errors.linkInstagram?.message ?? ''}
              rightIcon={(
                <span
                  className="text-medium text-gray-900 font-semibold cursor-pointer"
                  onClick={async () => {
                    try {
                      const text = await navigator.clipboard.readText();
                      setValue('linkInstagram', text, { shouldDirty: true, shouldValidate: true });
                    } catch (error) {
                      console.error(error);
                    }
                  }}
                >
                  Paste
                </span>
              )}
              {...field}
              onFocus={handleFocus}
              onBlur={() => handleBlur(onBlur)}
            />
          </div>
        )}
      />
      <Controller
        control={control}
        name="linkLinkedin"
        render={({ field: { onBlur, ...field }, formState: { errors } }) => (
          <div className="flex gap-4 items-center">
            <label htmlFor='linkedinURL-in'><Linkedin /></label>
            <Input
              id={"linkedinURL-in"}
              containerClassName="bg-white"
              className="bg-white"
              placeholder="https://linkedin.com/in/username"
              errorText={errors.linkLinkedin?.message ?? ''}
              rightIcon={(
                <span
                  className="text-medium text-gray-900 font-semibold cursor-pointer"
                  onClick={async () => {
                    try {
                      const text = await navigator.clipboard.readText();
                      setValue('linkLinkedin', text, { shouldDirty: true, shouldValidate: true });
                    } catch (error) {
                      console.error(error);
                    }
                  }}
                >
                  Paste
                </span>
              )}
              {...field}
              onFocus={handleFocus}
              onBlur={() => handleBlur(onBlur)}
            />
          </div>
        )}
      />
      <Controller
        control={control}
        name="linkTiktok"
        render={({ field: { onBlur, ...field }, formState: { errors } }) => (
          <div className="flex gap-4 items-center">
            <label htmlFor='tiktokURL-in'><Tiktok /></label>
            <Input
              id={"tiktokURL-in"}
              containerClassName="bg-white"
              className="bg-white"
              placeholder="https://tiktok.com/@username"
              errorText={errors.linkTiktok?.message ?? ''}
              rightIcon={(
                <span
                  className="text-medium text-gray-900 font-semibold cursor-pointer"
                  onClick={async () => {
                    try {
                      const text = await navigator.clipboard.readText();
                      setValue('linkTiktok', text, { shouldDirty: true, shouldValidate: true });
                    } catch (error) {
                      console.error(error);
                    }
                  }}
                >
                  Paste
                </span>
              )}
              {...field}
              onFocus={handleFocus}
              onBlur={() => handleBlur(onBlur)}
            />
          </div>
        )}
      />
      <Controller
        control={control}
        name="linkGithub"
        render={({ field: { onBlur, ...field }, formState: { errors } }) => (
          <div className="flex gap-4 items-center">
            <label htmlFor='githubURL-in'><Github /></label>
            <Input
              id={"githubURL-in"}
              containerClassName="bg-white"
              className="bg-white"
              placeholder="https://github.com/username"
              errorText={errors.linkGithub?.message ?? ''}
              rightIcon={(
                <span
                  className="text-medium text-gray-900 font-semibold cursor-pointer"
                  onClick={async () => {
                    try {
                      const text = await navigator.clipboard.readText();
                      setValue('linkGithub', text, { shouldDirty: true, shouldValidate: true });
                    } catch (error) {
                      console.error(error);
                    }
                  }}
                >
                  Paste
                </span>
              )}
              {...field}
              onFocus={handleFocus}
              onBlur={() => handleBlur(onBlur)}
            />
          </div>
        )}
      />
      <Controller
        control={control}
        name="linkCalendly"
        render={({ field: { onBlur, ...field }, formState: { errors } }) => (
          <div className="flex gap-4 items-center">
            <label htmlFor='calendlyURL-in'><Calendly /></label>
            <Input
              id={"calendlyURL-in"}
              containerClassName="bg-white"
              className="bg-white"
              placeholder="https://calendly.com/username"
              errorText={errors.linkCalendly?.message ?? ''}
              rightIcon={(
                <span
                  className="text-medium text-gray-900 font-semibold cursor-pointer"
                  onClick={async () => {
                    try {
                      const text = await navigator.clipboard.readText();
                      setValue('linkCalendly', text, { shouldDirty: true, shouldValidate: true });
                    } catch (error) {
                      console.error(error);
                    }
                  }}
                >
                  Paste
                </span>
              )}
              {...field}
              onFocus={handleFocus}
              onBlur={() => handleBlur(onBlur)}
            />
          </div>
        )}
      />
      {!!errors.root?.message && (
        <span className="text-small text-error-600">{errors.root?.message}</span>
      )}
      <AnimatedButton
        disabled={!isDirty}
        variant="primaryBrand"
        size="medium"
        type="submit"
        state={saveButtonState}
        successText="Saved!"
        successDuration={1700}
      >
        Save
      </AnimatedButton>
    </form>
  );
};

export default GeneralInformation;
